import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import {
  SelectDropdown,
  TextField,
  Typography,
} from '@zendesk/forethought-elements';
import { getEnabledChannelDropdownOptions } from './constants';
import { useDebouncedUpdateAbTestConfig } from './hooks';
import { generateAbTestConfigRequestBody } from './utils';
import { useGetAbTestConfigByIdQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import { SolveWidgetProduct } from 'src/types/types';
import { formatProductLabel } from 'src/utils/formatProductChannels';

const AbTestDetailSection = () => {
  const { t } = useTranslation('agent-ab-test');
  const { abTestId = '' } = useParams();

  const { data } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};

  const { handleAbTestConfigUpdate } = useDebouncedUpdateAbTestConfig();

  const { description = '', name = '', product, status } = data || {};

  const isEditingDisabled = status !== 'draft';

  const channelOptions = getEnabledChannelDropdownOptions(featureFlags);

  if (isEditingDisabled) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <ReadOnlyField label={t('detailSection.testNameLabel')} value={name} />
        <ReadOnlyField
          label={t('detailSection.descriptionLabel')}
          value={description || ''}
        />
        <ReadOnlyField
          label={t('detailSection.channelLabel')}
          value={
            product
              ? formatProductLabel(product)
              : t('detailSection.channelFallback')
          }
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <TextField
        error={name.trim() ? undefined : t('detailSection.testNameRequired')}
        label={t('detailSection.testNameLabel')}
        onChange={e => {
          if (!data) {
            return;
          }
          handleAbTestConfigUpdate(
            generateAbTestConfigRequestBody(data, { name: e.target.value }),
          );
        }}
        readOnly={isEditingDisabled}
        value={name}
      />
      <TextField
        error={
          description?.trim()
            ? undefined
            : t('detailSection.descriptionRequired')
        }
        label={t('detailSection.descriptionLabel')}
        multiline
        onChange={e => {
          if (!data) {
            return;
          }
          handleAbTestConfigUpdate(
            generateAbTestConfigRequestBody(data, {
              description: e.target.value,
              name,
            }),
          );
        }}
        readOnly={isEditingDisabled}
        value={description || ''}
      />
      <SelectDropdown
        error={!product ? t('detailSection.channelRequired') : undefined}
        helperText={t('detailSection.channelHelperText')}
        id='ab-test-channel-selector'
        inputProps={{ readOnly: isEditingDisabled }}
        label={t('detailSection.channelLabel')}
        onChange={e => {
          const newProduct = e.target.value as SolveWidgetProduct;
          if (!data || !newProduct) {
            return;
          }
          const channelChanged = product !== newProduct;
          handleAbTestConfigUpdate(
            generateAbTestConfigRequestBody(data, {
              product: newProduct,
              ...(channelChanged
                ? { intent_workflow_id: null, variants: [] }
                : {}),
            }),
            { immediate: true },
          );
        }}
        options={channelOptions}
        value={product || ''}
      />
    </Box>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography color={palette.colors.grey[600]} variant='font12'>
        {label}
      </Typography>
      <Typography variant='font14'>{value}</Typography>
    </Box>
  );
};

export default AbTestDetailSection;
